import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { GpsController, GpsState } from './gpsController.js';
import {requestGeolocationPermission as importPermission} from './locationService.js';

const read = (key:string) => { try { return localStorage.getItem(key); } catch { return null; } };
const save = (key:string,value:string) => { try { localStorage.setItem(key,value); } catch { /* Storage may be unavailable. */ } };
const restorePosition = (key:string):GeolocationPosition|null => {
  try {
    const position=JSON.parse(read(key)||'null');
    const {latitude,longitude}=position?.coords||{};
    return position && Number.isFinite(position.timestamp) && Date.now()-position.timestamp<240*60000
      && position.timestamp<=Date.now()+10000 && Number.isFinite(latitude) && Number.isFinite(longitude)
      && Math.abs(latitude)<=90 && Math.abs(longitude)<=180 ? position : null;
  } catch { return null; }
};
type Session={state:GpsState;enabled:boolean;allowed:boolean;foregroundEpoch:number;claimLocationReminder:()=>boolean;start:()=>Promise<void>;stop:()=>void};
const GpsSessionContext=createContext<Session|null>(null);
type Props={children:React.ReactNode;accountKey:string;allowed:boolean;enabledPreference?:boolean;
  allowAutomaticPrompt?:boolean;
  onEnabledChange?:(value:boolean)=>Promise<void>;
  api?:ConstructorParameters<typeof GpsController>[1]};

/** Owned by the signed-in workspace, never by a routed page or map. */
export function GpsSessionProvider({children,accountKey,allowed,enabledPreference,onEnabledChange,api,allowAutomaticPrompt=true}:Props) {
  const enabledKey=`gps:enabled:${accountKey}`;
  const positionKey=`gps:last-position:${accountKey}`;
  const [enabled,setEnabled]=useState(()=>read(enabledKey)!=='false');
  const [state,setState]=useState<GpsState>({permission:'prompt',status:'idle',position:null,error:null});
  const controller=useRef<GpsController|null>(null);
  const allowedRef=useRef(allowed);allowedRef.current=allowed;
  const [foregroundEpoch,setForegroundEpoch]=useState(0);
  const reminderEpoch=useRef(-1);
  const claimLocationReminder=useCallback(()=>{
    if(reminderEpoch.current===foregroundEpoch)return false;
    reminderEpoch.current=foregroundEpoch;return true;
  },[foregroundEpoch]);
  useEffect(()=>{setEnabled(enabledPreference??(read(enabledKey)!=='false'));},[enabledKey,enabledPreference]);
  useEffect(()=>{
    const gps=new GpsController(next=>{
      setState(next);
      if(next.position){
        const {coords,timestamp}=next.position;
        save(positionKey,JSON.stringify({timestamp,coords:{latitude:coords.latitude,longitude:coords.longitude,
          accuracy:coords.accuracy,altitude:coords.altitude,altitudeAccuracy:coords.altitudeAccuracy,
          heading:coords.heading,speed:coords.speed}}));
      }
    },api,restorePosition(positionKey));
    controller.current=gps;
    setState(gps.state);
    gps.setVisible(document.visibilityState!=='hidden');
    let hidden=document.visibilityState==='hidden';
    const visibility=()=>{const visible=document.visibilityState!=='hidden';gps.setVisible(visible);if(visible&&hidden){setForegroundEpoch(n=>n+1);void requestPermission();}hidden=!visible;};
    const requestPermission=async()=>{if(!api&&!navigator.permissions?.query){gps.recover();return;}const permission=await (api?.permission||importPermission)();if(!disposed)gps.permissionChanged(permission);};
    const recover=()=>gps.recover();
    const hide=()=>{hidden=true;gps.setVisible(false);};
    const show=()=>{gps.setVisible(true);if(hidden){hidden=false;setForegroundEpoch(n=>n+1);void requestPermission();}};
    document.addEventListener('visibilitychange',visibility);
    window.addEventListener('online',recover);
    window.addEventListener('pagehide',hide);
    window.addEventListener('pageshow',show);
    let permission:PermissionStatus|undefined;
    let disposed=false;
    const changed=()=>{if(permission)gps.permissionChanged(permission.state);};
    if(navigator.permissions?.query)void navigator.permissions.query({name:'geolocation'}).then(value=>{
      if(disposed)return;
      permission=value;
      permission.addEventListener('change',changed);
    }).catch(()=>{});
    return()=>{
      disposed=true;
      permission?.removeEventListener('change',changed);
      document.removeEventListener('visibilitychange',visibility);
      window.removeEventListener('online',recover);
      window.removeEventListener('pagehide',hide);
      window.removeEventListener('pageshow',show);
      gps.dispose();controller.current=null;
    };
  },[positionKey,api]);
  useEffect(()=>{
    if(allowed&&enabled&&enabledPreference!==false)void controller.current?.start(false,allowAutomaticPrompt);
    else controller.current?.stop();
  },[allowed,enabled,enabledPreference,positionKey,api,allowAutomaticPrompt]);
  const start=useCallback(async()=>{
    if(!allowed)return;
    await onEnabledChange?.(true);
    if(!allowedRef.current)return;
    save(enabledKey,'true');
    setEnabled(true);await controller.current?.start(true);
  },[allowed,enabledKey,onEnabledChange]);
  const stop=useCallback(()=>{save(enabledKey,'false');setEnabled(false);controller.current?.stop();void onEnabledChange?.(false).catch(()=>{});},[enabledKey,onEnabledChange]);
  return <GpsSessionContext.Provider value={{state,enabled,allowed,foregroundEpoch,claimLocationReminder,start,stop}}>{children}</GpsSessionContext.Provider>;
}
export function useGpsSession(){
  const session=useContext(GpsSessionContext);
  if(!session)throw new Error('GPS session requires the signed-in workspace provider');
  return session;
}
